import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { galleryImages, homepageContent } from '../../data/products';
import { useContent } from '../../context/ContentContext';
import { Reveal } from '../../motion';
import { ParallaxImg } from '../../motion3d/Depth';
import './Gallery.css';

/* Editorial photo grid with a simple lightbox. */
const Gallery = () => {
  const content = homepageContent.gallery;
  const { revision } = useContent();
  const [expanded, setExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index or null
  const openerRef = useRef(null);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => { setLightbox(null); }, [revision]);

  const shown = expanded ? galleryImages : galleryImages.slice(0, content.initialCount);

  const step = useCallback((d) => {
    setLightbox((i) => {
      if (i === null) return i;
      return (i + d + galleryImages.length) % galleryImages.length;
    });
  }, []);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'Tab') {
        const controls = dialogRef.current?.querySelectorAll('button, a[href]');
        if (!controls?.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      openerRef.current?.focus();
    };
  }, [lightbox, step]);

  if (!galleryImages.length) return null;

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-inner">
        <Reveal className="gallery-header">
          <div className="section-tag">{content.eyebrow}</div>
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </Reveal>

        <div className="gallery-grid">
          {shown.map((img, i) => (
            <motion.button
              key={img.url}
              type="button"
              className={`gallery-item ${img.span || ''}`}
              onClick={(event) => {
                openerRef.current = event.currentTarget;
                setLightbox(i);
              }}
              aria-label={`View ${img.label}`}
              initial={{ opacity: 0, y: 24, rotateX: 10 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.5, ease: 'easeOut', delay: (i % 3) * 0.05 } }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <ParallaxImg src={img.url} alt={img.label} loading="lazy" strength={14} />
              <span className="gallery-caption">{img.label}</span>
            </motion.button>
          ))}
        </div>

        {galleryImages.length > content.initialCount && (
          <div className="gallery-more">
            <button className="btn btn-secondary" onClick={() => setExpanded((e) => !e)}>
              {expanded ? 'Show fewer' : `View all ${galleryImages.length} photos`}
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox !== null && galleryImages[lightbox] && (
          <motion.div
            className="lightbox"
            onClick={() => setLightbox(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="lightbox-caption"
          >
            <motion.div
              ref={dialogRef}
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.94, y: 18, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 10, opacity: 0 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
            >
              <img src={galleryImages[lightbox].url} alt={galleryImages[lightbox].label} />
              <p id="lightbox-caption" className="lightbox-label">{galleryImages[lightbox].label}</p>
              <button ref={closeRef} className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close image">×</button>
              <button className="lightbox-nav prev" onClick={() => step(-1)} aria-label="Previous image">‹</button>
              <button className="lightbox-nav next" onClick={() => step(1)} aria-label="Next image">›</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
export default Gallery;
